import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { categoryAPI, collectionAPI } from '../services/api';

export default function Navbar() {
  const [openMenu, setOpenMenu] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const { cart } = useCart();
  const { user, logout } = useAuth();

  React.useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;

    const updateKeyboardState = () => {
      const isMobile = window.innerWidth <= 768;
      const keyboardIsOpen = isMobile && window.innerHeight - viewport.height > 140;
      setKeyboardOpen(keyboardIsOpen);
    };

    updateKeyboardState();
    viewport.addEventListener('resize', updateKeyboardState);
    window.addEventListener('resize', updateKeyboardState);
    return () => {
      viewport.removeEventListener('resize', updateKeyboardState);
      window.removeEventListener('resize', updateKeyboardState);
    };
  }, []);
  const cartCount = cart.reduce((total, item) => total + Number(item.quantity || 1), 0);

  React.useEffect(() => {
    categoryAPI.getAllCategories()
      .then((response) => setCategories(response.data.categories || []))
      .catch(() => setCategories([]));

    collectionAPI.getAllCollections()
      .then((response) => setCollections(response.data.collections || []))
      .catch(() => setCollections([]));
  }, []);

  const categoryGroups = ['Girl', 'Women', 'Boy/Men', 'Family Combo'].map((group) => ({
    title: group,
    items: categories.filter((category) => category.categoryGroup === group && category.parentCategory)
  })).filter((group) => group.items.length);

  const toggleMenu = (menuName) => {
    setOpenMenu((current) => (current === menuName ? '' : menuName));
  };

  const closeMenu = () => {
    setOpenMenu('');
    setMobileMenuOpen(false);
  };

  return (
    <nav className={`navbar ${keyboardOpen ? 'navbar-keyboard-hidden' : ''}`}>
      <div className="navbar-container">
        <div className="brand-block">
          <Link to="/" className="brand-name" onClick={closeMenu}>
            Starlight Threading
          </Link>
        </div>

        <button
          type="button"
          className="mobile-nav-toggle"
          aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={mobileMenuOpen}
          aria-controls="primary-navigation"
          onClick={() => setMobileMenuOpen((isOpen) => !isOpen)}
        >
          <span aria-hidden="true">{mobileMenuOpen ? '\u00d7' : '\u2630'}</span>
        </button>

        <div id="primary-navigation" className={`nav-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="nav-item nav-home">
            <Link to="/" onClick={closeMenu}>Home</Link>
          </div>

          <div className={`nav-item ${openMenu === 'categories' ? 'expanded' : ''}`}>
            <button type="button" className="nav-disclosure" aria-expanded={openMenu === 'categories'} onClick={() => toggleMenu('categories')}>Shop By Categories</button>
            <div className="nav-dropdown mega-dropdown">
              {categoryGroups.length ? categoryGroups.map((group) => <div key={group.title} className="dropdown-column"><h4>{group.title}</h4><ul>{group.items.map((category) => <li key={category._id}><Link to={`/shop?category=${category._id}`} onClick={closeMenu}>{category.name}</Link></li>)}</ul></div>) : <div className="dropdown-column"><h4>Categories</h4><ul><li>No info available</li></ul></div>}
            </div>
          </div>

          <div className={`nav-item ${openMenu === 'collections' ? 'expanded' : ''}`}>
            <button type="button" className="nav-disclosure" aria-expanded={openMenu === 'collections'} onClick={() => toggleMenu('collections')}>Shop By Collection</button>
            <div className="nav-dropdown mega-dropdown">
              {collections.length ? (
                <div className="dropdown-column" style={{ minWidth: 220 }}>
                  <h4>Collections</h4>
                  <ul>
                    {collections.map((collection) => (
                      <li key={collection._id}>
                        <Link to={`/shop?collection=${collection._id}`} onClick={closeMenu}>{collection.name}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="dropdown-column"><h4>Collections</h4><ul><li>No collections available</li></ul></div>
              )}
            </div>
          </div>

          <div className="nav-item nav-contact">
            <Link to="/contact" onClick={closeMenu}>Contact Us</Link>
          </div>

          <div className="nav-actions">
            <Link to="/account" className="icon-link" aria-label="Account" title="Account">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <circle cx="12" cy="8" r="3.25" />
                <path d="M5.5 20v-1.25A4.75 4.75 0 0 1 10.25 14h3.5a4.75 4.75 0 0 1 4.75 4.75V20" />
              </svg>
            </Link>
            {user && <button type="button" className="logout-button" onClick={logout}>Logout</button>}
            <Link to="/cart" className="icon-link cart-icon-link" aria-label={`Cart, ${cartCount} items`} title="Cart">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M5 8.5h14l-1 11H6l-1-11Z" />
                <path d="M8.5 8.5a3.5 3.5 0 0 1 7 0" />
              </svg>
              {cartCount > 0 && <span className="cart-count">{cartCount > 99 ? '99+' : cartCount}</span>}
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
